import { styled } from '@linaria/react';
import { Divider, Group, Checkbox as MantineCheckbox, Switch as MantineSwitch, Stack } from '@mantine/core';
import { baseWhite, inkLight, secondaryBase } from '../../styles/design-tokens';
import { Icon } from '../../ui-library/icon/icon';
import { Tooltip } from '../../ui-library/tooltip/tooltip';
import { Text } from '../../ui-library/typography/typography';
import type { FilterGroup, FilterGroupToggle } from './filter-panel.type';

type GroupProps<T extends FilterGroup> = {
  group: T;
  toggleCollapsed: (group: T) => void;
  isCollapsed: boolean;
  isAllSelected: boolean;
  handleAllChange: (group: T, isChecked: boolean) => void;
  handleIndividualChange: (group: T, value: string, isChecked: boolean) => void;
  hasDivider?: boolean;
  disabled?: boolean;
};

export const FilterPanelGroup = <T extends FilterGroup>({
  group,
  toggleCollapsed,
  isCollapsed,
  isAllSelected,
  handleAllChange,
  handleIndividualChange,
  hasDivider = true,
  disabled = false,
}: GroupProps<T>) => {
  const isDisabled = group.disabled ?? disabled;

  const groupHeader = (
    <Group justify="space-between" style={{ cursor: 'pointer' }} onClick={() => toggleCollapsed(group)}>
      <Group gap="xs">
        {group.icon && <Icon name={group.icon} size={16} color={inkLight} style={{ cursor: 'pointer' }} />}
        <Text weight="medium" size="small">
          {group.name}
        </Text>
        {group.tooltip && (
          <Tooltip label={group.tooltip} variant="black" multiline maw={300}>
            <Icon name="info" size={16} color={secondaryBase} />
          </Tooltip>
        )}
      </Group>
      <Icon
        name={isCollapsed ? 'unfold_more' : 'unfold_less'}
        size={16}
        color={inkLight}
        style={{ cursor: 'pointer' }}
      />
    </Group>
  );

  return (
    <Stack key={group.name} gap={16}>
      {groupHeader}
      {!isCollapsed && (
        <>
          {group.showAll !== false && (
            <Checkbox
              label={group.showAllLabel ?? 'All'}
              checked={isAllSelected}
              onChange={(event) => handleAllChange(group, event.currentTarget.checked)}
              disabled={isDisabled}
              styles={{
                label: {
                  cursor: isDisabled ? 'default' : 'pointer',
                },
              }}
            />
          )}
          {group.options.map((option) => (
            <Checkbox
              key={option.value}
              label={option.label}
              checked={group?.selectedValues?.includes(option.value)}
              onChange={(event) => handleIndividualChange(group, option.value, event.currentTarget.checked)}
              disabled={isDisabled}
              styles={{
                label: {
                  cursor: isDisabled ? 'default' : 'pointer',
                },
              }}
            />
          ))}
        </>
      )}
      {hasDivider && <Divider style={{ marginTop: '8px' }} />}
    </Stack>
  );
};

type ToggleProps<T extends FilterGroupToggle> = {
  toggle: T;
  hasDivider?: boolean;
};

export const FilterPanelToggle = <T extends FilterGroupToggle>({ toggle, hasDivider = true }: ToggleProps<T>) => {
  return (
    <Stack key={toggle.name} gap={16}>
      <Group justify="space-between">
        <Group gap="xs">
          {toggle.icon && <Icon name={toggle.icon} size={20} color={inkLight} />}
          <Text weight="medium" size="small">
            {toggle.name}
          </Text>
        </Group>
        <Switch
          aria-label={toggle.name}
          checked={toggle.selectedValue}
          onChange={() => toggle.onSelectionChange(!toggle.selectedValue)}
          color={secondaryBase}
        />
      </Group>
      {hasDivider && <Divider style={{ marginTop: '8px' }} />}
    </Stack>
  );
};

const Checkbox = styled(MantineCheckbox)`
  .mantine-Checkbox-input:checked {
    background-color: var(--secondary-base);
    border-color: var(--secondary-base);
  }
  .mantine-Checkbox-input:indeterminate {
    background-color: var(--secondary-base);
    border-color: var(--secondary-base);
  }
  .mantine-Checkbox-input:disabled {
    background-color: var(--sky-light);
    border-color: var(--sky-light);
    opacity: 0.5;
    cursor: default;
  }
  .mantine-Checkbox-input:disabled:checked {
    background-color: var(--sky-lighter);
    border-color: var(--sky-base);
  }
  .mantine-Checkbox-label:has(+ .mantine-Checkbox-input:disabled) {
    color: var(--sky-dark);
  }
`;

const Switch = styled(MantineSwitch)`
  .mantine-Switch-track {
    height: 25px;
    width: 45px;
    padding: 2px 8px;
    border-radius: 100px;
    border: 2px solid rgba(29, 27, 32, 0.12);
    background: rgba(231, 224, 236, 0.12);
    cursor: pointer;
    box-sizing: border-box;
  }

  .mantine-Switch-thumb {
    height: 14px;
    width: 14px;
    border-radius: 24px;
    margin-left: 2px;
    opacity: 0.38;
    background: #1d1b20;
  }

  input {
    &:checked + .mantine-Switch-track {
      border-color: ${secondaryBase};
      background: ${secondaryBase};

      .mantine-Switch-thumb {
        height: 16px;
        width: 16px;
        background: ${baseWhite};
        z-index: 1;
        left: 22px;
        opacity: 1;
      }
    }
  }
`;
